import { useCallback, useRef, useMemo, useState } from "react"
import { Loader, Button, Title, Text, Panel } from "dash-styled"
import { NavButton } from "../../../../../../../../../Common/NavButton/NavButton"
import { routePaths } from "../../../../../../../../../../Routes"
import { createBaselineFromEntries } from "../../../../../../../../../../Actions/API/AppInstance/AppInstanceBaselineDataActions"
import { CreateFileUrlLink } from "../../../../../../../Common/CreateFileUrlLink/CreateFileUrlLink"
import {
    clearInputsForId,
    changeInputValueWithValidation
} from "../../../../../../../../../../Actions/Client/InputRender/InputRenderActions"
import { push } from "connected-react-router"
import { useAppState } from "../../../../../../../../../../Hooks/useReduxState"
import { useDispatch } from "react-redux"
import { AppDetailBase } from "../../../../../AppDetailBase/AppDetailBase"
import { useAppInstance } from "../../../../../../../../../../Hooks/useAppInstance"
import { useAppTemplate } from "../../../../../../../../../../Hooks/useAppTemplate"
import { hasValue } from "../../../../../../../../../../Models/Client/FetchState"
import {
    ExampleDataProvider,
    ExampleData
} from "../../../../../../../../../Providers/ExampleDataProvider/ExampleDataProvider"
import { useBoolean } from "../../../../../../../../../../Hooks/useBoolean"
import { LabelingLayout } from "../../../../../../../../../Common/LabelingLayout/LabelingLayout"
import { useUrlSigner } from "../../../../../../../../../../Hooks/useUrlSigner"
import { useFetchState } from "../../../../../../../../../../Hooks/useFetchState"
import { DataAccess } from "../../../../../../../../../../Models/Client/DataAccess"
import { useMLActionProcessor } from "../../../../../../../../../../Hooks/useMLActionProcessor"
import { DataPointEditApp } from "../../../../../../../../../Common/Lazy/DataPointEditApp"
import { MainThunkDispatch } from "../../../../../../../../../../Reducers"
import { canSubmit } from "../../../../../../../../../../Reducers/Client/InputRender/InputRenderReducer"
import { InputFormRenderContainer } from "../../../../../../../../../Common/InputFormRenderContainer/InputFormRenderContainer"
import { useAppSchema } from "../../../../../../../../../../Hooks/useAppSchema"
import { InputFormRender } from "../../../../../../../../../Common/InputFormRender/InputFormRender"
import type { SchemaFormData } from "resha"
import type { SchemaForm } from "universal-schema/lib"

const INPUT_FORM_NAME = (appID: string) => `${appID}_add_baseline_item_input`
const OUTPUT_FORM_NAME = (appID: string) => `${appID}_add_baseline_item_output`

interface SingleProps {
    orgUsername: string
    appID: string
}

export const BaselineDataSingle = ({ orgUsername, appID }: SingleProps) => {
    const [isOutputEditorOpen, openOutputEditor, closeOutputEditor] =
        useBoolean(false)
    const inputFormID = INPUT_FORM_NAME(appID)
    const outputFormID = OUTPUT_FORM_NAME(appID)
    const dispatch = useDispatch<MainThunkDispatch>()
    const instanceFetch = useAppInstance(appID)
    const templateFetch = useAppTemplate(appID)
    const schemaFetch = useAppSchema(appID, false)
    const inputs = useAppState(
        state => state.client.inputRender.entries[inputFormID]
    )
    const outputs = useAppState(
        state => state.client.inputRender.entries[outputFormID]
    )
    const getSchemaFormData = useCallback(
        (schema: {
            input: SchemaForm
            output: SchemaForm
        }): { input: SchemaFormData; output: SchemaFormData } => ({
            input: { ...schema.input, formData: inputs?.values },
            output: { ...schema.output, formData: outputs?.values }
        }),
        [inputs, outputs]
    )
    const schemaFormDataFetch = useFetchState(schemaFetch, getSchemaFormData)

    const [saving, setSaving] = useState<boolean>(false)

    const addData = useCallback(
        async (appID: string) => {
            if (!hasValue(templateFetch)) {
                return
            }
            try {
                setSaving(true)
                await dispatch(
                    createBaselineFromEntries(
                        appID,
                        inputFormID,
                        outputFormID,
                        templateFetch.value.inputSchema,
                        templateFetch.value.outputSchema
                    )
                )
                dispatch(clearInputsForId(INPUT_FORM_NAME(appID)))
                dispatch(clearInputsForId(OUTPUT_FORM_NAME(appID)))
                dispatch(
                    push(
                        routePaths.appCustomizeBaselineData(orgUsername, appID)
                    )
                )
            } finally {
                setSaving(false)
            }
        },
        [dispatch, inputFormID, outputFormID, templateFetch, orgUsername]
    )

    const handleSubmit = useCallback(() => {
        if (!hasValue(templateFetch)) {
            return
        }

        addData(appID)
    }, [templateFetch, appID, addData])

    const toolbarRef = useRef<HTMLDivElement>(null)
    const footerRef = useRef<HTMLDivElement>(null)
    const dataAccess = useMemo(() => DataAccess.perGT(appID), [appID])
    const urlSigner = useUrlSigner(dataAccess)
    const mlActionProcessor = useMLActionProcessor(dataAccess)

    const handleCloseOutputEditor = useCallback(
        async (value: unknown) => {
            if (!hasValue(templateFetch)) {
                return
            }

            dispatch(
                changeInputValueWithValidation(
                    outputFormID,
                    value,
                    templateFetch.value.outputSchema,
                    false
                )
            )
            closeOutputEditor()
        },
        [closeOutputEditor, dispatch, outputFormID, templateFetch]
    )
    const renderSidebar = () => {
        return (
            <div>
                <Button
                    variant="contained"
                    color="darkviolet"
                    onClick={handleSubmit}
                    disabled={
                        saving || !canSubmit(outputs) || !canSubmit(inputs)
                    }
                    marginBottom="3"
                >
                    {saving ? "Submitting..." : "Submit Data"}
                </Button>
                <CreateFileUrlLink orgUsername={orgUsername} />
                <NavButton
                    variant="text"
                    color="plain"
                    className="my-4"
                    to={routePaths.appCustomizeBaselineDataAddDataCurl(
                        orgUsername,
                        appID
                    )}
                >
                    Create ground truth data programmatically
                </NavButton>
                <NavButton
                    variant="text"
                    color="plain"
                    marginBottom="3"
                    to={routePaths.appCustomizeBaselineDataAddDataUpload(
                        orgUsername,
                        appID
                    )}
                >
                    Create ground truth data from JSON
                </NavButton>
            </div>
        )
    }
    const baseProps = {
        orgUsername,
        id: appID,
        title: "Add ground-truth data",
        breadcrumbs: ["Ground-truth Data"],
        renderSidebar,
        fixedContentWidth: true
    }

    if (
        !hasValue(instanceFetch) ||
        !hasValue(templateFetch) ||
        !hasValue(schemaFormDataFetch)
    ) {
        return (
            <AppDetailBase {...baseProps}>
                <Loader />
            </AppDetailBase>
        )
    }

    const editCapability =
        templateFetch.value.metadata.edit_output ?? "labeling"
    const render = (example?: ExampleData) => {
        const isOutputEditable =
            editCapability !== "annotation" ||
            (inputs !== undefined &&
                inputs.errors.length === 0 &&
                (inputs.formErrors === undefined ||
                    inputs.formErrors.length === 0))

        // if instance doesn't have completed job (or bot init?), don't render
        if (!example || !example.output || !example.isValidJob)
            return (
                <AppDetailBase {...baseProps}>
                    <Title variant="heading1">Create ground truth data</Title>
                    <Text>
                        You need a processed data point to create ground truth
                        using this form.{" "}
                    </Text>
                    <NavButton
                        to={routePaths.appAddData(orgUsername, appID, "input")}
                    >
                        Click here to add data point.
                    </NavButton>
                </AppDetailBase>
            )

        if (isOutputEditorOpen) {
            return (
                <LabelingLayout
                    orgUsername={orgUsername}
                    mode="create_groundtruth"
                    instanceFetch={instanceFetch}
                    toolbar={<div ref={toolbarRef} />}
                >
                    <DataPointEditApp
                        errorMessage="Error rendering ground-truth data"
                        input={schemaFormDataFetch.value.input}
                        output={schemaFormDataFetch.value.output}
                        urlSigner={urlSigner}
                        mlActionProcessor={mlActionProcessor}
                        toolbarRef={toolbarRef}
                        footerRef={footerRef}
                        saveButtonLabel="Back"
                        uploadPathPrefix="ground-truth/new/assets"
                        resetAnnotation={outputs?.values === undefined}
                        fallback={<Loader absolute={true} mask={false} />}
                        onSave={handleCloseOutputEditor}
                    />
                </LabelingLayout>
            )
        }

        return (
            <AppDetailBase {...baseProps}>
                <Text className="pb-8">
                    Use this to quickly add ground truth data to your data
                    program. This only allows you to create one instance of
                    ground truth data at a time. If you want to create more
                    either upload JSON or create ground truth data
                    programmatically.
                </Text>
                <section className="pb-8">
                    <Title variant="heading2" component="h3" weight="semibold">
                        Inputs
                    </Title>
                    <InputFormRenderContainer
                        id={inputFormID}
                        {...schemaFormDataFetch.value.input}
                        defaultData={example.input}
                    />
                </section>
                <section className="pb-8">
                    <Title variant="heading2" component="h3" weight="semibold">
                        Output
                    </Title>
                    {editCapability === "annotation" ? (
                        <Panel>
                            <InputFormRender
                                {...schemaFormDataFetch.value.output}
                                skipMissingDataField={true}
                            />
                            {!isOutputEditable && (
                                <p className="mb-4 text-center">
                                    Fill out input fields to start editing
                                    output
                                </p>
                            )}
                            <Button
                                color="plain"
                                className="mx-auto w-64 mb-2"
                                disabled={!isOutputEditable}
                                onClick={openOutputEditor}
                            >
                                Edit output
                            </Button>
                        </Panel>
                    ) : (
                        <InputFormRenderContainer
                            id={outputFormID}
                            {...schemaFormDataFetch.value.output}
                            defaultData={example.output}
                        />
                    )}
                </section>
            </AppDetailBase>
        )
    }

    return (
        <ExampleDataProvider
            render={render}
            instance={instanceFetch.value}
            template={templateFetch.value}
        />
    )
}
